import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, forkJoin  } from 'rxjs';
import { switchMap, catchError, map, concatMap } from 'rxjs/operators';

import { ActivityReportForSubmitModel, ActivityReportExtendedModel, WorkingPeriodResponce, CustomerNotificationsResponse, RecognizedActivityReportModel, ZipCodeModel, ParsedFileModel } from '@shared/models';
import { ActivityReportExtended     } from '@shared/factories';
import { UserService, HelperService } from '@shared/services';

import { environment } from 'environments/environment';

interface ZipCodeResponseModel {
  zip_code: ZipCodeModel;
}

interface RecognizedTimeFramesResponseModel {
  recognized_working_period: RecognizedActivityReportModel;
}

@Injectable()
export class WorkingPeriodService {
  private get ASSIGNMENTS_API(): string { return `${environment.apiUrl}api/portal/v3/assignments`             };
  private get WP_DETAILS_API():  string { return `${environment.apiUrl}api/portal/v3/working_periods`         };
  private get WP_REVIEW_API():   string { return `${environment.apiUrl}api/portal/v3/working_periods_review`  };
  private get WP_EXPORTS_API():  string { return `${environment.apiUrl}api/portal/v3/working_periods_exports` };
  private get WP_REPORTS_API():  string { return `${environment.apiUrl}api/portal/v3/working_periods_reports` };

  constructor (
    private http:          HttpClient,
    private userService:   UserService,
    private helperService: HelperService,
  ) { }

  updateWorkingPeriod(periodId: number, data, suf: string = '') {
    return this.http.put<{success: boolean, errors?:string[]}>(`${this.WP_REVIEW_API}/${periodId}/${suf}`, data)
    .pipe(
      switchMap((res) => {
        if (res.success) return of(res.success);
        else return throwError(res.errors.join('; '))
      })
    );
  }

  approveWorkingPeriod(periodId: number, data) {
    return this.updateWorkingPeriod(periodId, data, 'internal_approve');
  }

  releaseWorkingPeriod(periodId: number, data) {
    return this.updateWorkingPeriod(periodId, data, 'customer_release');
  }

  rejectWorkingPeriod(periodId: number, data) {
    return this.updateWorkingPeriod(periodId, data, 'customer_reject');
  }

  deleteWorkingPeriod(periodId: number, data) {
    return this.updateWorkingPeriod(periodId, data, 'internal_reject');
  }

  archiveWorkingPeriod(periodId: number, data) {
    return this.updateWorkingPeriod(periodId, data, 'internal_archive');
  }

  manuallyResolveWorkingPeriod(periodId: number, data = null): Observable<{} | never> {
    return this.http.put<{success:boolean, errors?:string[]}>(`${this.WP_EXPORTS_API}/${periodId}/resolve_manually`, data )
    .pipe(
      switchMap((responce) => {
        if (responce.success) return of({});
        else return throwError(responce.errors.join('; '));
      })
    );
  }

  submitWorkingPeriod(ar: ActivityReportForSubmitModel): Observable<ActivityReportExtended[] | never> {
    return this.http.post<{success: boolean, errors?:string[], working_period?:ActivityReportExtendedModel}[]>(this.WP_DETAILS_API, { working_periods:[ar] })
    .pipe(
      switchMap((res) => {
        if (Array.isArray(res) && !res.filter(ar => !ar.success).length) return of(res.map(ar => new ActivityReportExtended(ar.working_period)));
        else {
          let userLocked = res.filter(e => e.errors).find(r => r.errors.find((e: any) => e.code === 'external_employee_locked'));
          if (userLocked) return throwError('external_employee_locked');
          else            return throwError(res.filter(e => e.errors).map(e => e.errors.map((e: any) => e.title).join('; ')));
        }
      })
    );
  }

  internalCheck(periodId: number, comment: string, angle: number = null): Observable<any> {
    let data: any = { working_period: { internal_employee_notes: comment }};
    if (angle) data.working_period.photo_tn_rotation_angle = angle;

    return this.http.put<{success:boolean, errors?:string[], working_period?:ActivityReportExtendedModel}>(`${this.WP_REVIEW_API}/${periodId}/checked_time`, data )
    .pipe(
      switchMap((responce) => {
        if (responce.success) return of({});
        else return throwError(responce.errors.join('; '));
      })
    );
  }

  getWorkingPeriodById(periodId: number): Observable<ActivityReportExtended> {
    return forkJoin([
      this.requestWorkingPeriod(periodId),
      this.requestCustomerNotifications(periodId)
    ]).pipe(
      concatMap(res => forkJoin([
        of(res[0]),
        of(res[1]),
        this.requestZipCode(res[0]),
        this.requestOCRData(res[0])
      ])),
      map(data => new ActivityReportExtended(
        data[0].working_period,
        data[1]?.customers_notifications,
        data[2]?.zip_code,
        data[3]?.recognized_working_period
      ))
    );
  }

  private requestWorkingPeriod(periodId: number): Observable<WorkingPeriodResponce> {
    return this.http.get<WorkingPeriodResponce>(`${this.WP_DETAILS_API}/${periodId}`);
  }

  private requestCustomerNotifications(periodId: number): Observable<CustomerNotificationsResponse> {
    if (this.userService.isInternal) return this.http.get<CustomerNotificationsResponse>(`${this.WP_DETAILS_API}/${periodId}/customers_notifications`).pipe(catchError(err => of(null)));
    return of(null);
  }

  private requestZipCode(wp: WorkingPeriodResponce): Observable<ZipCodeResponseModel> {
    if (this.userService.isInternal && !wp.working_period.archived_at) return this.http.get<ZipCodeResponseModel>(`${this.ASSIGNMENTS_API}/${wp.working_period.assignment.id}/zip_code`).pipe(catchError(err => of(null)));
    return of(null);
  }

  private requestOCRData(wp: WorkingPeriodResponce): Observable<RecognizedTimeFramesResponseModel> {
    if (this.userService.isInternal && HelperService.checkIfValidPhotoTn(wp.working_period) && !wp.working_period.archived_at) return this.http.get<RecognizedTimeFramesResponseModel>(`${this.WP_DETAILS_API}/${wp.working_period.id}/recognized_working_period`).pipe(catchError(err => of(null)));
    return of(null);
  }

  recognizedTimeFrames(periodId: number): Observable<any> {
    let calls = [];
    calls.push(this.http.get<WorkingPeriodResponce>(`${this.WP_DETAILS_API}/${periodId}/recognized_time_frames`));
    return forkJoin(calls);
  }

  getReportPDFAsBlob(timeFormat: string, pageFormat: string, reportId: number): Observable<ParsedFileModel> {
    return this.helperService.loadFileAsBlobByUrl(`${this.WP_REPORTS_API}/${reportId}?time_format=${timeFormat}&page_format=${pageFormat}`, 'application/pdf');
  }

}
